<script lang="ts" setup>
import { useLangHelpers } from "@modules/energy-manager/composables/useLangHelpers"
import { FREQ_AVAILABLE_BY_VARIABLES } from "@modules/energy-manager/constants"
import { useEmConsoCompareStore } from "@modules/energy-manager/stores/em-conso-compare.store"
import { useVModel } from "@vueuse/core"
import { computed, watch } from "vue"
import { useI18n } from "vue-i18n"
import { getMinFreqAvailableByVariable } from "@modules/energy-manager/helpers"
import { Freq } from "@types"

type Props = {
    /**
     * Fréquence de comparaison
     */
    freq: Freq
    /**
     * Variable à comparer
     */
    variable: string
}

const props = defineProps<Props>()

const emConsoCompareStore = useEmConsoCompareStore()

const { buildWarningFreq, buildInfoFreq } = useLangHelpers()

const { t } = useI18n({
    messages: {
        fr: {
            label: "Fréquence de comparaison",
            info: {
                title: "Fréquence indisponible",
                content: ({ named }: { named: (key: string) => string }) => {
                    return named("sentence")
                },
            },
            warn: {
                title: ({ named }: { named: (key: string) => string }) => {
                    const disabled = named("disabled") ?? false

                    return disabled
                        ? "Fréquence indisponible"
                        : "Période restreinte"
                },
                content: ({ named }: { named: (key: string) => string }) => {
                    return named("sentence")
                },
            },
            freq_unvailable:
                "La fréquence {freq} n'est pas disponible | Les fréquences {freq} ne sont pas disponibles",
        },
    },
})

// Liste des fréquences possibles
const FREQ = [
    { label: t("freq.hourly.long"), value: "hourly" },
    { label: t("freq.daily.long"), value: "daily" },
    { label: t("freq.weekly.long"), value: "weekly" },
    { label: t("freq.monthly.long"), value: "monthly" },
    // { label: t("freq.yearly.long"), value: "yearly" },
]

const emit = defineEmits(["update:freq"])

const freq = useVModel(props, "freq", emit, {
    eventName: "update:freq",
})

/**
 * Récupère la liste des fréquences en fonction de la variable sélectionné
 * et de la configuration d'affichage
 */
const getFreqOptionListByDisplayDataConfig = computed(() => {
    const variableConfig = FREQ_AVAILABLE_BY_VARIABLES.find(
        (v) => v.variable === props.variable.split(".")[0]
    )

    return Object.entries(
        emConsoCompareStore.getDisplayDataConfig?.config_periods ?? {}
    )?.map(([key, value]) => {
        // le label de la fréquence
        const label = FREQ.find((f) => f.value === key)?.label
        // Indique si la fréquence est désactivé
        let disabled = false
        // le type de message à afficher
        let severity = null
        // indique si un message doit être affiché
        let message = false
        // Vérifie si la fréquence n'est pas disponible pour la variable
        // rend l'option désactivé avec un message informatif
        if (!variableConfig?.frequencyConfigs?.includes(key as Freq)) {
            disabled = true
            severity = "info"
        }
        // Si la fréquence est désactivé car trop de données
        // rend l'option désactivé avec un message de warning
        else if (value.disabled) {
            disabled = true
            severity = "warn"
        } else if (value.max) {
            message = true
            severity = "warn"
        }

        return {
            label,
            value: key,
            disabled,
            severity,
            message,
        }
    })
})

const getVariableKey = computed(() => {
    return props.variable.split(".")[0]
})

function getSentenceByFreq(option: { value: string; severity: string }) {
    if (option.severity === "info") {
        return buildInfoFreq(
            getVariableKey.value,
            option.value,
            getMinFreqAvailableByVariable(getVariableKey.value)
        )
    }
    return buildWarningFreq(
        emConsoCompareStore.getDisplayDataConfig?.config_periods ?? {},
        option.value,
        emConsoCompareStore.getSourcesCount
    )
}

/**
 * Écoute le changement de la fréquence
 * pour mettre à jour le choix de raccourci de période si celui-ci n'est pas disponible
 */
watch(
    () => props.variable,
    () => {
        // Vérifie si la fréquence est disponible pour la variable
        if (
            getFreqOptionListByDisplayDataConfig.value.find(
                (f) => f.value === freq.value
            )?.disabled
        ) {
            // Met la première fréquence disponible
            freq.value = getFreqOptionListByDisplayDataConfig.value.find(
                (f) => !f.disabled
            )?.value as Freq
        }
    },
    {
        immediate: true,
    }
)
</script>

<template>
    <BaseInputWrapper input-id="freq" class="dashboard-card-compare-freq">
        <template #header
            ><BaseIcon icon="fal fa-circle-2" color="var(--p-primary-700)"
        /></template>
        <BaseSelect
            input-id="freq"
            v-model="freq"
            :options="getFreqOptionListByDisplayDataConfig"
            option-label="label"
            option-value="value"
            option-disabled="disabled"
            checkmark
            :highlight-on-select="false"
        >
            <template #option="slotProps">
                <div class="freq-option-item">
                    <p>{{ slotProps.option.label }}</p>
                    <BaseTooltipMessage
                        @click.prevent.stop
                        v-if="
                            slotProps.option.message ||
                            slotProps.option.disabled
                        "
                        :severity="slotProps.option.severity"
                        :title="
                            t(`${slotProps.option.severity}.title`, {
                                disabled: slotProps.option.disabled,
                            })
                        "
                        :content="
                            t(`${slotProps.option.severity}.content`, {
                                sentence: getSentenceByFreq(slotProps.option),
                            })
                        "
                    />
                </div>
            </template>
        </BaseSelect>
    </BaseInputWrapper>
</template>

<style scoped>
.freq-option-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}
</style>
